<script lang="ts">
  import { t } from '../../lib/i18n/index.svelte.js'
  import type { ListStore } from '../../lib/state/listStore.svelte.js'
  import { toasts } from '../../lib/state/toasts.svelte.js'
  import { canWebShare, copyText, listAsText, webShare, whatsappUrl } from '../../lib/utils/share.js'
  import Button from '../ui/Button.svelte'
  import Icon from '../ui/Icon.svelte'
  import Sheet from '../ui/Sheet.svelte'

  interface Props {
    open: boolean
    store: ListStore
    onclose: () => void
  }
  const { open, store, onclose }: Props = $props()

  let tab = $state<'edit' | 'view'>('edit')
  let copied = $state(false)

  const links = $derived(store.list?.links)
  const hasEdit = $derived(Boolean(links?.edit))
  const active = $derived(tab === 'edit' && hasEdit ? links?.edit : links?.view)
  const title = $derived(store.list?.title ?? '')

  $effect(() => {
    if (open && !hasEdit) tab = 'view'
  })

  async function copy(): Promise<void> {
    if (!active) return
    if (await copyText(active)) {
      copied = true
      toasts.show(t('toast_copied'))
      setTimeout(() => (copied = false), 2000)
    }
  }

  async function copyAsText(): Promise<void> {
    if (!store.list) return
    const body = listAsText(store.list.title, store.list.emoji, store.items)
    if (await copyText(body)) toasts.show(t('toast_text_copied'))
  }
</script>

<Sheet {open} title={t('share_title')} {onclose}>
  {#if hasEdit}
    <div class="tabs" role="tablist">
      <button role="tab" aria-selected={tab === 'edit'} onclick={() => (tab = 'edit')}>
        {t('share_edit_tab')}
      </button>
      <button role="tab" aria-selected={tab === 'view'} onclick={() => (tab = 'view')}>
        {t('share_view_tab')}
      </button>
    </div>
  {/if}

  <p class="warning">
    <Icon name={tab === 'edit' && hasEdit ? 'key' : 'eye'} size={16} />
    {tab === 'edit' && hasEdit ? t('share_edit_warning') : t('share_view_warning')}
  </p>

  <div class="link">
    <input readonly value={active ?? ''} onfocus={(e) => e.currentTarget.select()} aria-label="URL" />
    <Button variant="primary" onclick={copy}>
      {copied ? t('share_copied') : t('share_copy')}
    </Button>
  </div>

  <div class="actions">
    <a class="action" href={whatsappUrl(title, active ?? '')} target="_blank" rel="noopener" data-native>
      {t('share_whatsapp')}
    </a>
    {#if canWebShare()}
      <button class="action" onclick={() => webShare(title, active ?? '')}>{t('share_more')}</button>
    {/if}
    <button class="action" onclick={copyAsText}>{t('menu_copy_text')}</button>
  </div>

  <p class="tip">{t('share_self_tip')}</p>
</Sheet>

<style>
  .tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
    padding: 2px;
    border-radius: var(--radius);
    background: var(--surface-2);
  }
  .tabs button {
    min-height: 40px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    font-weight: 550;
    color: var(--fg-muted);
  }
  .tabs button[aria-selected='true'] {
    background: var(--surface);
    color: var(--fg);
    box-shadow: var(--shadow-1);
  }
  .warning {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: var(--space-4) 0 var(--space-3);
    font-size: 14px;
    color: var(--fg-muted);
  }
  .link {
    display: flex;
    gap: var(--space-2);
  }
  .link input {
    flex: 1;
    min-width: 0;
    padding: 0 var(--space-3);
    min-height: 44px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-2);
    font-size: 14px;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-top: var(--space-4);
  }
  .action {
    flex: 1 1 auto;
    min-height: 44px;
    padding: 0 var(--space-4);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    color: var(--fg);
    font-weight: 550;
    text-decoration: none;
  }
  .action:hover {
    background: var(--surface-2);
  }
  .tip {
    margin-top: var(--space-4);
    font-size: 13px;
    color: var(--fg-faint);
  }
</style>
